export interface PeriodData {
    id?: number;
    academic_year_id?: number;
    institution_id?: number;
    name: string;
    start_week: number;
    end_week: number;
    start_week_year?: number;
    end_week_year?: number;
    start_date?: string;
    end_date?: string;
    year?: number;
    color?: string;
    is_active?: boolean;
    active_schedule_draft_id?: number | null;
    activeScheduleDraftId?: number | null;
    createdAt?: string;
    updatedAt?: string;
    // Academic year relation data if included
    academicYear?: {
        id: number;
        academic_year: string;
        start_week: number;
        end_week: number;
    };
}

class Period {
    id?: number;
    academic_year_id?: number;
    institution_id?: number;
    name: string;
    start_week: number;
    end_week: number;
    start_week_year?: number;
    end_week_year?: number;
    start_date?: string;
    end_date?: string;
    year?: number;
    color?: string;
    is_active?: boolean;
    active_schedule_draft_id?: number | null;
    activeScheduleDraftId?: number | null;
    createdAt?: string;
    updatedAt?: string;
    academicYear?: {
        id: number;
        academic_year: string;
        start_week: number;
        end_week: number;
    };

    constructor(data: PeriodData) {
        this.id = data.id;
        this.academic_year_id = data.academic_year_id;
        this.institution_id = data.institution_id;
        this.name = data.name;
        this.start_week = data.start_week;
        this.end_week = data.end_week;
        this.start_week_year = data.start_week_year;
        this.end_week_year = data.end_week_year;
        this.start_date = data.start_date;
        this.end_date = data.end_date;
        this.year = data.year;
        this.color = data.color || '#3B82F6';
        this.is_active = data.is_active !== undefined ? data.is_active : true;
        this.active_schedule_draft_id = data.active_schedule_draft_id ?? data.activeScheduleDraftId ?? null;
        this.activeScheduleDraftId = data.activeScheduleDraftId ?? data.active_schedule_draft_id ?? null;
        this.createdAt = data.createdAt;
        this.updatedAt = data.updatedAt;
        this.academicYear = data.academicYear;
    }

    // Static method to create a Period instance from raw API data
    static fromApiData(data: any): Period {
        const startWeekYear = data.start_week_year ?? data.startWeekYear;
        const endWeekYear = data.end_week_year ?? data.endWeekYear;

        return new Period({
            id: data.id,
            academic_year_id: data.academic_year_id,
            institution_id: data.institution_id,
            name: data.name || 'Period',
            start_week: data.start_week ?? data.startWeek ?? 1,
            end_week: data.end_week ?? data.endWeek ?? 53,
            start_week_year: startWeekYear,
            end_week_year: endWeekYear,
            start_date: data.start_date ?? data.startDate,
            end_date: data.end_date ?? data.endDate,
            year: data.year ?? startWeekYear,
            color: data.color,
            is_active: data.is_active,
            active_schedule_draft_id: data.active_schedule_draft_id ?? data.activeScheduleDraftId ?? null,
            activeScheduleDraftId: data.activeScheduleDraftId ?? data.active_schedule_draft_id ?? null,
            createdAt: data.created_at || data.createdAt,
            updatedAt: data.updated_at || data.updatedAt,
            academicYear: data.academicYear
        });
    }

    // Static method to create multiple Period instances from raw API data
    static fromApiDataArray(dataArray: any[]): Period[] {
        if (!dataArray || !Array.isArray(dataArray)) {
            return [];
        }
        return dataArray.map(data => Period.fromApiData(data));
    }

    // Method to convert Period instance to plain object for API calls
    toApiData() {
        return {
            name: this.name,
            start_week: this.start_week,
            end_week: this.end_week,
            start_week_year: this.start_week_year,
            end_week_year: this.end_week_year,
            year: this.year,
            color: this.color,
            is_active: this.is_active,
            active_schedule_draft_id: this.active_schedule_draft_id ?? this.activeScheduleDraftId ?? null
        };
    }

    // Helper method to get a formatted display string
    getDisplayString(): string {
        const yearStr = this.crossesYears() ? `${this.getEffectiveYear()}-${this.getEffectiveYear() + 1}` : this.getEffectiveYear().toString();
        return `${this.name} (Week ${this.start_week}-${this.end_week}, ${yearStr})`;
    }

    // Helper method to get the effective year (from year field, academicYear, or current year)
    getEffectiveYear(): number {
        if (this.year != null) return this.year;
        if (this.start_week_year != null) return this.start_week_year;
        if (this.academicYear) {
            // Extract the start year from academic year string like "2024-2025"
            const yearMatch = this.academicYear.academic_year.match(/^(\d{4})/);
            if (yearMatch && yearMatch[1]) return parseInt(yearMatch[1], 10);
        }
        return new Date().getFullYear();
    }

    // Helper method to check if a week number falls within this period
    containsWeek(weekNumber: number, year?: number): boolean {
        const effectiveYear = year || this.getEffectiveYear();
        
        if (this.end_week >= this.start_week) {
            // Normal period within the same year
            return year === effectiveYear && weekNumber >= this.start_week && weekNumber <= this.end_week;
        } else {
            // Cross-year period
            if (year === effectiveYear) {
                return weekNumber >= this.start_week;
            } else if (year === effectiveYear + 1) {
                return weekNumber <= this.end_week;
            }
            return false;
        }
    }

    // Helper method to get the duration in weeks
    getDurationInWeeks(): number {
        if (this.end_week >= this.start_week) {
            // Normal period within the same year
            return this.end_week - this.start_week + 1;
        } else {
            // Cross-year period: from start_week to end of year + from start of year to end_week
            return (53 - this.start_week + 1) + this.end_week;
        }
    }

    // Helper method to check if this period crosses years
    crossesYears(): boolean {
        return this.end_week < this.start_week;
    }

    // Calculate start date (first Monday of start_week)
    getStartDate(): Date {
        // If we have start_date from API, use it
        if (this.start_date) {
            return new Date(this.start_date);
        }
        // Otherwise calculate from week number
        return this.getDateOfWeek(this.getEffectiveYear(), this.start_week);
    }

    // Calculate end date (last Sunday of end_week)
    getEndDate(): Date {
        // If we have end_date from API, use it
        if (this.end_date) {
            return new Date(this.end_date);
        }
        // Otherwise calculate from week number
        const endYear = this.crossesYears() ? this.getEffectiveYear() + 1 : this.getEffectiveYear();
        const endDate = this.getDateOfWeek(endYear, this.end_week);
        endDate.setDate(endDate.getDate() + 6); // Add 6 days to get to end of week (Sunday)
        return endDate;
    }

    // Helper method to get the date of the Monday of a given week
    private getDateOfWeek(year: number, week: number): Date {
        // January 4th is always in week 1 according to ISO week numbering
        const jan4 = new Date(year, 0, 4);
        const dayOfWeek = jan4.getDay() || 7; // Convert Sunday (0) to 7
        const weekStart = new Date(jan4);
        weekStart.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
        return weekStart;
    }

    // Helper method to check if a date falls within this period
    containsDate(date: Date): boolean {
        const startDate = this.getStartDate();
        const endDate = this.getEndDate();
        return date >= startDate && date <= endDate;
    }

    // Get date range as an object
    getDateRange() {
        return {
            startDate: this.getStartDate(),
            endDate: this.getEndDate()
        };
    }

    // Helper method to check if this period is currently active
    isCurrentlyActive(): boolean {
        return this.is_active !== false; // Default to true if not specified
    }

    // Get display color
    getDisplayColor(): string {
        return this.color || '#3B82F6';
    }
}

export default Period; 